
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLineChat, setOpenLineUserId } from '../hooks/useLineChat';
import { useLineFilters } from '../hooks/useLineFilters';
import { playNotificationSound } from '../lib/soundUtils';
import LineChatPanel from './LineChatPanel';
import {
    Dialog, DialogContent, DialogTitle, IconButton,
    Switch, Button, Tooltip, Avatar, Box, Typography, TextField, MenuItem as MuiMenuItem, Select, FormControl, InputLabel,
    Snackbar, Alert, Popover, Menu, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChatIcon from '@mui/icons-material/Chat';
import RefreshIcon from '@mui/icons-material/Refresh';
import PushPinIcon from '@mui/icons-material/PushPin';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import ViewListIcon from '@mui/icons-material/ViewList';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { reservationSyncService } from '../services/reservationSyncService';
import { saveDispatchReservation, fetchDispatchVehicles } from '../api/dispatchService';
import { useReservationStatuses } from '../hooks/useReservationStatuses';
import { toEditorReservation } from '../lib/editorDataTransform';
import ReservationEditorPanel from './editor2';

import {
    DndContext, 
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './LineChat.css';
import TemplateHelpDialog from './TemplateHelpDialog';
import BoardHelpDialog from './BoardHelpDialog';

// --- DnD Sortable Components ---
const SortableItem = ({ id, children, isSelected, onClick, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 1,
        position: 'relative',
        padding: '12px',
        borderRadius: '8px',
        cursor: 'pointer',
        marginBottom: '8px',
        background: isSelected ? '#eff6ff' : 'white',
        border: isSelected ? '1px solid #3b82f6' : '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    };

    return (
        <div ref={setNodeRef} style={style} onClick={onClick}>
            <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#94a3b8' }}>
                <DragIndicatorIcon fontSize="small" />
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
                {children}
            </div>
            {onDelete && (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ color: '#ef4444' }}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )}
        </div>
    );
};

const CategorySortableItem = ({ id, name, isSelected, onClick, onDelete, onEdit }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        padding: '8px 12px',
        borderRadius: '8px',
        cursor: 'pointer',
        marginBottom: '4px',
        background: isSelected ? '#eef2ff' : 'transparent',
        border: isSelected ? '1px solid #6366f1' : '1px solid transparent',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    };

    return (
        <div ref={setNodeRef} style={style} onClick={onClick}>
            <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#cbd5e1' }}>
                <DragIndicatorIcon fontSize="small" />
            </div>
            <div style={{ flex: 1, fontWeight: 800, fontSize: '14px', color: isSelected ? '#4f46e5' : '#475569' }}>
                {name}
            </div>
            <Box display="flex">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                    <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                    <DeleteIcon fontSize="small" color="error" />
                </IconButton>
            </Box>
        </div>
    );
};

// --- UserAvatar Component (Simplified sync on mount) ---
const LazyAvatar = ({ user, syncProfile, sx, ...props }) => {
    const [hasAttemptedSync, setHasAttemptedSync] = useState(false);
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        const userName = user.custom_display_name || user.line_display_name;
        // const isTarget = userName && userName.includes('テスト太郎'); // Debugging target

        if (hasAttemptedSync) return;

        const syncWithRetry = async (currentRetry = 0) => {
            const success = await syncProfile(user.line_user_id);

            if (success) {
                setHasAttemptedSync(true);
            } else if (currentRetry < 3) {
                setTimeout(() => syncWithRetry(currentRetry + 1), 3000);
            } else {
                setHasAttemptedSync(true);
            }
        };

        // マウント時にランダムな遅延（0-1000ms）を置いて同期を開始（大量リクエスト防止）
        const delay = Math.floor(Math.random() * 1000);
        const timer = setTimeout(() => syncWithRetry(0), delay);

        return () => clearTimeout(timer);
    }, [user.line_user_id, hasAttemptedSync, syncProfile]);

    const initials = user.custom_display_name?.[0] || user.line_display_name?.[0] || '?';
    const LINE_API_URL = process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';
    const API_KEY = 'nc-line-sync-key-2024';
    const PROXY_VERSION = 'v5'; // Cache busting

    const proxiedUrl = (user.line_picture_url && user.line_picture_url.startsWith('http') && !imgError)
        ? `${LINE_API_URL}?action=proxy_image&url=${encodeURIComponent(user.line_picture_url)}&api_key=${API_KEY}&v=${PROXY_VERSION}` 
        : null;

    return (
        <div className="line-avatar-container">
            <Avatar 
                src={proxiedUrl} 
                imgProps={{ crossOrigin: "anonymous" }}
                onError={(e) => {
                    // const userName = user.system_customer_name || user.line_display_name;
                    // console.warn(`[Avatar] Image failed for ${userName} (${user.line_user_id}). URL: ${proxiedUrl}`);
                    setImgError(true);
                }}
                sx={{ width: 40, height: 40, bgcolor: '#f1f5f9', color: '#64748b', ...sx }}
                {...props}
            >
                {initials}
            </Avatar>
            <div className={`link-status-badge ${user.linked_reservations?.length > 0 ? 'connected' : 'disconnected'}`}>
                {user.linked_reservations?.length > 0 ? <LinkIcon sx={{ fontSize: 'inherit' }} /> : <LinkOffIcon sx={{ fontSize: 'inherit' }} />}
            </div>
        </div>
    );
};

const getAlertColor = (level: number): string | null => {
    switch (level) {
        case 1: return '#2563eb'; // Blue
        case 2: return '#f59e0b'; // Yellow
        case 3: return '#ef4444'; // Red
        default: return null;
    }
};

const getAlertTooltip = (level: number): string => {
    switch (level) {
        case 1: return 'AI保留中（ルールによりホールド）';
        case 2: return '料金・スケジュール変更の相談あり';
        case 3: return '緊急：空港到着・配車対応が必要';
        default: return '';
    }
};

const getAlertLabel = (level: number): string => {
    switch (level) {
        case 1: return 'AI保留';
        case 2: return '相談あり';
        case 3: return '緊急';
        default: return '';
    }
};

const LineChatBoard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        users, tags, optimisticMessages, fetchUsers, loading, togglePin, toggleArchive, sendBroadcast, syncProfile,
        fetchTemplates, saveTemplate, deleteTemplate,
        fetchTemplateCategories, saveTemplateCategory, deleteTemplateCategory,
        updateCategoryOrder, updateTemplateOrder, fetchTags, linkReservation,
        pusherConnected
    } = useLineChat();
    const { reservationStatusList, vehicleTypeList } = useReservationStatuses();
    
    // Reservation Editor State
    const [selectedResForEditor, setSelectedResForEditor] = useState(null);
    const [editingResForEditor, setEditingResForEditor] = useState(null);
    const [vehiclesCache, setVehiclesCache] = useState({});
    const [isSavingRes, setIsSavingRes] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // reservationSyncServiceの変更を購読して予約データ更新時にchatInstancesを再計算
    useEffect(() => {
        const unsub = reservationSyncService.subscribe(() => {
            setRefreshTrigger(prev => prev + 1);
        });
        return () => { if (typeof unsub === 'function') unsub(); };
    }, []);

    const handleStaffChatRegistration = (instance) => {
        setStaffRegTarget(instance);
        setStaffRegName('');
    };

    const confirmStaffRegistration = async () => {
        if (!staffRegTarget || !staffRegName.trim()) return;
        const success = await linkReservation(staffRegTarget.line_user_id, `STAFF:${staffRegName.trim()}`);
        setStaffRegTarget(null);
        if (success) {
            showMessage('スタッフチャットとして登録しました');
            fetchUsers();
        } else {
            showMessage('登録に失敗しました', 'error');
        }
    };

    // NEW: Expand users (X) to chat instances (Y)
    const chatInstances = React.useMemo(() => {
        const instances = [];
        const cachedReservations = reservationSyncService.getReservations();
        
        const today = new Date();
        const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.getFullYear() + '-' + String(tomorrow.getMonth() + 1).padStart(2, '0') + '-' + String(tomorrow.getDate()).padStart(2, '0');

        users.forEach(user => {
            const resIds = user.reservation_ids || [];
            if (resIds.length === 0) {
                // 紐付けなしユーザーはそのまま表示
                instances.push({
                    ...user,
                    instance_reservation_id: null,
                    instanceKey: `${user.line_user_id}_none`,
                    hasOtherReservations: false,
                    reservation_statuses: []
                });
            } else {
                // すべての紐付けがSTAFFチャットの場合は、リスト自体に表示させない
                const hasRealReservations = resIds.some(rid => !String(rid).startsWith('STAFF:'));
                if (!hasRealReservations && resIds.length > 0) return;

                resIds.forEach(rid => {
                    // STAFFチャット（"STAFF:"から始まるID）はボードには表示しない
                    if (String(rid).startsWith('STAFF:')) return;

                    // reservationSyncServiceのみから予約データ取得（DBキャッシュ不使用）
                    const resData = cachedReservations.find(r => String(r.id) === String(rid)) || null;

                    // バッジを最新データで計算
                    const newBadges: string[] = [];
                    if (resData) {
                        const status = resData.status || resData.status_code;
                        const pDate = (resData.start_date || '').split(' ')[0].split('T')[0];
                        const rDate = (resData.end_date || '').split(' ')[0].split('T')[0];

                        if (status === 'in_use' || status === 'in-use' || status === '利用中') newBadges.push('利用中');
                        if (pDate === todayStr) newBadges.push('本日貸出');
                        if (rDate === todayStr) newBadges.push('本日返却');
                        if (pDate === tomorrowStr) newBadges.push('明日貸出');
                        if (rDate === tomorrowStr) newBadges.push('明日返却');
                    }

                    instances.push({
                        ...user,
                        instance_reservation_id: rid,
                        instance_res_data: resData,
                        instanceKey: `${user.line_user_id}_${rid}`,
                        hasOtherReservations: resIds.length > 1,
                        reservation_statuses: newBadges
                    });
                });
            }
        });
        return instances;
    }, [users, refreshTrigger]);

    // Tag filter state (must be before useLineFilters)
    const [selectedTagFilters, setSelectedTagFilters] = useState([]);
    const [tagFilterAnchor, setTagFilterAnchor] = useState(null);

    const {
        filters, toggleFilter, searchQuery, setSearchQuery,
        filteredUsers: filteredInstances,
        customDateFilter, setCustomDateFilter
    } = useLineFilters(chatInstances, JSON.parse(localStorage.getItem('line_filters') || '[]'), selectedTagFilters);

    // Update selection to use instanceKey
    const [selectedInstanceKey, setSelectedInstanceKey] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(() => localStorage.getItem('line_auto_refresh') === 'on');
    const [readMode, setReadMode] = useState(() => localStorage.getItem('line_read_mode') === 'on');
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('line_view_mode') || 'grid');
    const [showHelp, setShowHelp] = useState(false);
    const [showBoardHelp, setShowBoardHelp] = useState(false);
    const [multiSelectMode, setMultiSelectMode] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [broadcastText, setBroadcastText] = useState('');
    const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
    const [broadcastStep, setBroadcastStep] = useState<'compose' | 'preview'>('compose');
    const [preparedMessages, setPreparedMessages] = useState<{ uid: string; text: string; name: string; instanceKey: string }[]>([]);
    const [previewPage, setPreviewPage] = useState(0);
    const [visibleCount, setVisibleCount] = useState(50);
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const [showArchived, setShowArchived] = useState(() => localStorage.getItem('line_show_archived') === 'on');
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [staffRegTarget, setStaffRegTarget] = useState(null);
    const [staffRegName, setStaffRegName] = useState('');
    const [searchIncludedArchived, setSearchIncludedArchived] = useState(false);
    const [archiveToasts, setArchiveToasts] = useState([]);
    const autoArchiveRanRef = useRef(false);

    // 継続的なユーザー更新と新着検知
    const lastUsersRef = useRef(users);
    
    useEffect(() => {
        // モーダル表示中や一括選択中、検索中などは背景の自動更新を一時停止して安定性を高める
        if (!autoRefresh || searchQuery || multiSelectMode || selectedInstanceKey) return;

        // Pusher接続中は60秒（フォールバック）、切断時は15秒
        const pollInterval = pusherConnected ? 60000 : 15000;

        const interval = setInterval(async () => {
            const updatedUsers = await fetchUsers('', showArchived);

            if (updatedUsers && Array.isArray(updatedUsers)) {
                // Pusher未接続時のみポーリングで新着検知＋通知音
                if (!pusherConnected) {
                    const hasNewMessage = updatedUsers.some(newUser => {
                        const oldUser = lastUsersRef.current.find(u => u.line_user_id === newUser.line_user_id);
                        if (!oldUser) return newUser.unread_count > 0;
                        return (newUser.unread_count > oldUser.unread_count) ||
                               (newUser.last_message_at && newUser.last_message_at > oldUser.last_message_at);
                    });
                    if (hasNewMessage) {
                        playNotificationSound();
                    }
                }
                lastUsersRef.current = updatedUsers;
            }
        }, pollInterval);

        return () => clearInterval(interval);
    }, [autoRefresh, searchQuery, fetchUsers, showArchived, pusherConnected]);

    // usersが外部から更新された場合もRefを同期
    useEffect(() => {
        lastUsersRef.current = users;
    }, [users]);

    // Pusher: archived user got a new message — re-fetch to bring them into the list
    useEffect(() => {
        const handler = () => fetchUsers('', showArchived);
        window.addEventListener('line-pusher-refetch', handler);
        return () => window.removeEventListener('line-pusher-refetch', handler);
    }, [fetchUsers, showArchived]);

    // Auto-archive: 3日以上会話がない非ピン留めチャットをアーカイブ（ランディング時に1回だけ）
    useEffect(() => {
        if (autoArchiveRanRef.current || !users || users.length === 0) return;

        // reservationSyncServiceの予約データがロードされるまで待つ
        const allReservations = reservationSyncService.getReservations();
        if (allReservations.length === 0) return; // 予約データ未ロード時はスキップ（次回再評価）

        autoArchiveRanRef.current = true;

        const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);

        const staleUsers = users.filter(u => {
            if (u.is_pinned || u.is_archived) return false;
            // in_use ステータスの予約がある場合は除外（reservationSyncServiceから取得）
            const resIds = u.reservation_ids || [];
            const hasInUse = resIds.some(rid => {
                const r = allReservations.find(cr => String(cr.id) === String(rid));
                return r && (r.status === 'in_use' || r.status === 'in-use');
            });
            if (hasInUse) return false;
            // 紐付け予約がある場合はアーカイブしない
            if (resIds.length > 0) return false;
            if (!u.last_message_at) return true;
            return new Date(u.last_message_at) < threeDaysAgo;
        });

        if (staleUsers.length === 0) return;

        // 非同期でアーカイブ実行
        (async () => {
            for (const u of staleUsers) {
                await toggleArchive(u.line_user_id, true);
                const name = u.custom_display_name || u.line_display_name || u.line_user_id;
                setArchiveToasts(prev => [{ id: `auto_${Date.now()}_${u.line_user_id}`, name }, ...prev].slice(0, 5));
            }
        })();
    }, [users, toggleArchive]);

    // Archive toast auto-dismiss (3 seconds)
    useEffect(() => {
        if (archiveToasts.length === 0) return;
        const timer = setTimeout(() => {
            setArchiveToasts(prev => prev.slice(0, -1));
        }, 3000);
        return () => clearTimeout(timer);
    }, [archiveToasts]);

    // Persistence
    useEffect(() => {
        localStorage.setItem('line_auto_refresh', autoRefresh ? 'on' : 'off');
    }, [autoRefresh]);

    useEffect(() => {
        localStorage.setItem('line_view_mode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        localStorage.setItem('line_read_mode', readMode ? 'on' : 'off');
    }, [readMode]);

    useEffect(() => {
        if (filters.length > 0) {
            localStorage.setItem('line_filters', JSON.stringify(filters));
        } else {
            localStorage.removeItem('line_filters');
        }
    }, [filters]);

    // Apply persisted filters on mount
    useEffect(() => {
        const savedFilters = localStorage.getItem('line_filters');
        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);
                if (Array.isArray(parsed)) {
                    // We need to set them via toggleFilter or directly if we have access to setter
                    // Since useLineFilters only returns toggleFilter, we might need to update the hook
                    // Or just use the setter if we exposure it.
                    // For now, let's just use toggleFilter repeatedly or update the hook.
                    parsed.forEach(f => {
                        if (!filters.includes(f)) toggleFilter(f);
                    });
                }
            } catch (e) {
                console.error('Failed to parse saved filters', e);
            }
        }
    }, []);
    
    
    const [showTemplateManager, setShowTemplateManager] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editTemplate, setEditTemplate] = useState({ name: '', content: '', category_id: null });
    
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [editCategory, setEditCategory] = useState({ id: null, name: '' });
    const [selectedCategoryId, setSelectedCategoryId] = useState(null); // Filter for template list

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const showMessage = (message, severity = 'success') => setNotification({ open: true, message, severity });
    const handleCloseNotification = () => setNotification(prev => ({ ...prev, open: false }));

    const [dateAnchor, setDateAnchor] = useState(null);
    const [tempDate, setTempDate] = useState(new Date().toISOString().split('T')[0]);

    const handleSelectAll = () => {
        setSelectedUserIds(filteredInstances.map(i => i.instanceKey));
    };
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const loadTemplatesData = useCallback(async () => {
        const [t, c] = await Promise.all([
            fetchTemplates(),
            fetchTemplateCategories()
        ]);
        setTemplates(t);
        setCategories(c);
    }, [fetchTemplates, fetchTemplateCategories]);

    useEffect(() => {
        if (showTemplateManager) {
            loadTemplatesData();
        }
    }, [showTemplateManager, loadTemplatesData]);

    useEffect(() => {
        // 初期状態（検索なし）では通常通り取得
        if (!searchQuery) {
            fetchUsers('', showArchived);
        }
    }, [fetchUsers, searchQuery, showArchived]);

    // 検索語句がある場合はデバウンスしてサーバーサイド検索を実行（2段階検索）
    useEffect(() => {
        if (!searchQuery) {
            setSearchIncludedArchived(false);
            return;
        }
        const timer = setTimeout(async () => {
            setSearchIncludedArchived(false);
            // 1段階目: アーカイブ除外で検索（showArchivedがONなら最初から含む）
            const results = await fetchUsers(searchQuery, showArchived);
            // 2段階目: 結果0件 && アーカイブ未表示 → アーカイブ含めて再検索
            if ((!results || results.length === 0) && !showArchived) {
                const deepResults = await fetchUsers(searchQuery, true);
                if (deepResults && deepResults.length > 0) {
                    setSearchIncludedArchived(true);
                }
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery, fetchUsers, showArchived]);

    // Load vehicles for editor
    useEffect(() => {
        const loadVehicles = async () => {
            try {
                const data = await fetchDispatchVehicles();
                if (Array.isArray(data)) {
                    const vMap = {};
                    data.forEach(v => { vMap[String(v.id)] = v; });
                    setVehiclesCache(vMap);
                }
            } catch (err) {
                console.error('Failed to load vehicles for editor', err);
            }
        };
        loadVehicles();
    }, []);

    // Sync editing residue when selectedRes changes
    // LINE の instance_res_data は不完全な場合があるので、キャッシュから完全データを補完
    // アニメーションはReservationEditor内部で制御（ID変化を検知して閉じ→開き）
    useEffect(() => {
        if (selectedResForEditor) {
            const id = String(selectedResForEditor.id || selectedResForEditor.reservation_id || '');
            const cached = id ? reservationSyncService.cachedReservations?.get(id) : null;
            const fullData = cached ? { ...cached, ...selectedResForEditor } : selectedResForEditor;
            setEditingResForEditor(toEditorReservation(fullData));
        } else {
            setEditingResForEditor(null);
        }
    }, [selectedResForEditor]);

    const handleSaveReservation = async (updated: any, options: any = {}) => {
        const { keepOpen = false } = options;
        const dataToSave = updated || editingResForEditor;
        if (isSavingRes) {
            console.warn('[LineChatBoard] Already saving, skipping duplicate request');
            return;
        }

        setIsSavingRes(true);
        try {
            const result = await saveDispatchReservation(dataToSave);
            showMessage('予約を更新しました');
            
            // 重要: キャッシュを強制更新
            if (result && result.id) {
                reservationSyncService.updateSingleReservation(result);
                // 再計算を強制するためにトリガーを更新
                setRefreshTrigger(prev => prev + 1);
            }

            // 先にパネルを閉じて、その後にリフレッシュ処理を行う
            if (!keepOpen) {
                setSelectedResForEditor(null);
            }

            // 非同期でユーザー情報を更新（これによってリストが再描画される）
            fetchUsers(searchQuery, true); 
        } catch (err) {
            console.error('[LineChatBoard] Failed to save reservation:', err);
            showMessage('保存に失敗しました: ' + (err.message || ''), 'error');
        } finally {
            // ステートのリセット（わずかに遅延させて、後続の副作用との競合を避ける）
            setTimeout(() => {
                setIsSavingRes(false);
            }, 0);
        }
    };

    // 無限スクロール用：スクロール位置を監視して表示件数を増やす
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200) {
                setVisibleCount(prev => {
                    if (prev < filteredInstances.length) {
                        return prev + 30;
                    }
                    return prev;
                });
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [filteredInstances.length]);

    // フィルターや検索条件が変わった時は表示件数をリセット
    useEffect(() => {
        setVisibleCount(50);
    }, [filters, searchQuery, customDateFilter]);
    
    /* プロフィール同期は LazyAvatar (IntersectionObserver) により表示時に自動で行われるため、ここでの一斉同期は不要 */
    
    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const handleTemplateDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const visibleTemplates = templates.filter(t => 
            selectedCategoryId === null ? true : 
            selectedCategoryId === 'none' ? t.category_id === null : 
            String(t.category_id) === String(selectedCategoryId)
        ).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        
        const oldIndex = visibleTemplates.findIndex(t => String(t.id) === String(active.id));
        const newIndex = visibleTemplates.findIndex(t => String(t.id) === String(over.id));
        
        if (oldIndex !== -1 && newIndex !== -1) {
            const reorderedVisible = arrayMove(visibleTemplates, oldIndex, newIndex);
            
            // 1. Update master templates list WITH NEW ARRAY ORDER
            const newTemplates = [...templates];
            
            // We need to actually move the items in the master list to match the visual order
            // or at least update all sort_order properties correctly.
            reorderedVisible.forEach((t, idx) => {
                const masterIdx = newTemplates.findIndex(mt => String(mt.id) === String(t.id));
                if (masterIdx !== -1) {
                    newTemplates[masterIdx] = { ...t, sort_order: idx };
                }
            });
            
            setTemplates(newTemplates);

            const orders = reorderedVisible.map((t, idx) => ({
                id: String(t.id),
                sort_order: idx,
                category_id: t.category_id
            }));
            await updateTemplateOrder(orders);
        }
    };

    const handleCategoryDragEnd = async (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = categories.findIndex(c => String(c.id) === String(active.id));
            const newIndex = categories.findIndex(c => String(c.id) === String(over.id));
            
            const newCategories = arrayMove(categories, oldIndex, newIndex);
            setCategories(newCategories);

            const orders = newCategories.map((c, idx) => ({
                id: c.id,
                sort_order: idx
            }));
            await updateCategoryOrder(orders);
        }
    };

    const handleSaveCategory = async () => {
        if (!editCategory.name.trim()) return;
        const success = await saveTemplateCategory(editCategory);
        if (success) {
            showMessage('カテゴリを保存しました');
            setShowCategoryForm(false);
            setEditCategory({ id: null, name: '' });
            loadTemplatesData();
        } else {
            showMessage('カテゴリの保存に失敗しました', 'error');
        }
    };

    const handleDeleteCategory = async (id) => {
        if (window.confirm('このカテゴリを削除しますか？（属するテンプレートは未分類になります）')) {
            const success = await deleteTemplateCategory(id);
            if (success) {
                showMessage('カテゴリを削除しました');
                loadTemplatesData();
            } else {
                showMessage('カテゴリの削除に失敗しました', 'error');
            }
        }
    };

    const handleRefresh = useCallback(() => {
        fetchUsers(searchQuery || '', showArchived);
    }, [fetchUsers, searchQuery, showArchived]);

    const handleUserSelect = (instanceKey) => {
        setSelectedInstanceKey(instanceKey);
        // 通知音制御: 今開いているユーザーIDを共有シングルトンに伝える
        const lineUserId = instanceKey ? instanceKey.split('_')[0] : null;
        setOpenLineUserId(lineUserId);
        if (isMobile) setMobileMenuAnchor(null);
    };

    const toggleUserSelection = (instanceKey) => {
        setSelectedUserIds(prev => 
            prev.includes(instanceKey) ? prev.filter(id => id !== instanceKey) : [...prev, instanceKey]
        );
    };

    // 変数置換ヘルパー
    const formatBroadcastDateTime = (dtStr) => {
        if (!dtStr) return '';
        const d = new Date(dtStr);
        return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    const formatBroadcastTime = (dtStr) => {
        if (!dtStr) return '';
        const d = new Date(dtStr);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    // 変数置換してプレビュー用メッセージを生成
    const generatePreparedMessages = () => {
        return selectedUserIds.map(instanceKey => {
            const instance = chatInstances.find(i => i.instanceKey === instanceKey);
            const uid = instanceKey.split('_')[0];
            let replacedText = broadcastText;
            let customerName = 'お客様';

            if (instance) {
                const res = instance.instance_res_data;
                customerName = res?.customer_name || instance.custom_display_name || instance.line_display_name || 'お客様';

                const vars = {
                    name: customerName,
                    customer_name: customerName,
                    id: res?.id || '',
                    reservation_id: res?.id || '',
                    start_date: formatBroadcastDateTime(res?.start_date),
                    end_date: formatBroadcastDateTime(res?.end_date),
                    start_date_time: formatBroadcastTime(res?.start_date),
                    end_date_time: formatBroadcastTime(res?.end_date),
                    start_time: formatBroadcastTime(res?.start_date),
                    end_time: formatBroadcastTime(res?.end_date),
                    status: res?.status_name || '',
                    form: res?.id ? `https://n-car.jp/sys/form/?id=${res.id}` : ''
                };

                Object.keys(vars).forEach(key => {
                    const regex = new RegExp(`\\{\\s*${key}\\s*\\}`, 'gi');
                    replacedText = replacedText.replace(regex, vars[key]);
                });
            }

            return { uid, text: replacedText, name: customerName, instanceKey };
        });
    };

    // プレビューを表示
    const handleShowPreview = () => {
        if (!broadcastText.trim() || selectedUserIds.length === 0) return;
        const msgs = generatePreparedMessages();
        setPreparedMessages(msgs);
        setPreviewPage(0);
        setBroadcastStep('preview');
    };

    // プレビュー画面から送信
    const handleSendBroadcast = async () => {
        if (preparedMessages.length === 0) return;

        setIsSendingBroadcast(true);
        const apiMessages = preparedMessages.map(m => ({ uid: m.uid, text: m.text }));
        const results = await sendBroadcast(selectedUserIds, broadcastText, apiMessages);
        setIsSendingBroadcast(false);

        if (results && (results.success_count !== undefined)) {
            setNotification({
                open: true,
                message: `${results.success_count}件の送信に成功しました。${results.failed_count}件失敗。`,
                severity: results.failed_count > 0 ? 'warning' : 'success'
            });

            if (results.failed_count > 0) {
                console.error('[DEBUG-BULK] Some messages failed to send:', results.errors);
            }

            setShowBroadcastModal(false);
            setBroadcastStep('compose');
            setMultiSelectMode(false);
            setSelectedUserIds([]);
            setBroadcastText('');
            setPreparedMessages([]);
        } else {
            console.error('[DEBUG-BULK] Unexpected broadcast response:', results);
            setNotification({
                open: true,
                message: '送信プロセスの実行中に予期しないエラーが発生しました。',
                severity: 'error'
            });
        }
    };

    const renderChatCard = (instance) => {
        const isSelected = selectedUserIds.includes(instance.instanceKey);
        const user = instance; // For compatibility within the function
        const resData = instance.instance_res_data;
        return (
            <div 
                key={instance.instanceKey} 
                className={`line-chat-card ${user.unread_count > 0 ? 'unread' : ''} ${multiSelectMode && isSelected ? 'selected' : ''}`}
                onClick={(e) => {
                    if (e.metaKey) {
                        handleStaffChatRegistration(instance);
                    } else if (multiSelectMode) {
                        toggleUserSelection(instance.instanceKey);
                    } else {
                        handleUserSelect(instance.instanceKey);
                    }
                }}
            >
                {multiSelectMode && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
                        <div style={{ 
                            width: '20px', 
                            height: '20px', 
                            borderRadius: '50%', 
                            border: '2px solid #cbd5e1',
                            background: isSelected ? '#2563eb' : 'white',
                            borderColor: isSelected ? '#2563eb' : '#cbd5e1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px'
                        }}>
                            {isSelected && '✓'}
                        </div>
                    </div>
                )}
                
                {/* 左上のバッジ群 */}
                <div className="line-card-badges-top-left">
                    {user.hasOtherReservations && (
                        <div className="line-mini-badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>
                            他予約有
                        </div>
                    )}
                    {user.reservation_statuses?.map((st, idx) => {
                        const classMap = {
                            '本日貸出': 'today-pickup',
                            '本日返却': 'today-return',
                            '明日貸出': 'tomorrow-pickup',
                            '明日返却': 'tomorrow-return',
                            '利用中': 'in-use'
                        };
                        const cls = classMap[st] || st.toLowerCase().replace(/\s+/g, '-');
                        return (
                            <div key={idx} className={`line-mini-badge ${cls}`}>
                                {st}
                            </div>
                        );
                    })}
                    {user.alert_level > 0 && (
                        <Tooltip title={getAlertTooltip(user.alert_level)}>
                            <div className="line-mini-badge" style={{
                                background: getAlertColor(user.alert_level) || undefined,
                                color: 'white',
                                border: 'none',
                            }}>
                                {getAlertLabel(user.alert_level)}
                            </div>
                        </Tooltip>
                    )}
                </div>

                {user.unread_count > 0 && <div className="line-card-badge">{user.unread_count}</div>}

                <div className="line-card-pin-toggle">
                    <Tooltip title={user.is_pinned ? "ピン留めを解除" : "ピン留めする"}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                togglePin(user.line_user_id, !user.is_pinned);
                            }}
                            sx={{
                                color: user.is_pinned ? '#f97316' : '#94a3b8',
                                '&:hover': { color: '#f97316' }
                            }}
                        >
                            <PushPinIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                
                <div className="line-user-header">
                    <LazyAvatar 
                        user={user}
                        syncProfile={syncProfile}
                        sx={{ width: 44, height: 44, borderRadius: '14px' }}
                    />
                    <div className="line-user-meta">
                        <div className="line-user-name-container">
                            <div className="line-primary-name-row">
                                <div className="line-primary-name">
                                    {resData?.customer_name || user.custom_display_name || user.line_display_name || 'Unknown'}
                                </div>
                            </div>
                            {(() => {
                                const primaryName = resData?.customer_name || user.custom_display_name;
                                const secondaryName = user.line_display_name;
                                return primaryName && secondaryName && primaryName !== secondaryName ? (
                                    <div className="line-secondary-name">
                                        {secondaryName}
                                    </div>
                                ) : null;
                            })()}
                        </div>
                        {instance.instance_reservation_id && resData && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div className="line-usage-period">
                                    {(() => {
                                        const parseDate = (dateStr) => {
                                            if (!dateStr || typeof dateStr !== 'string') return null;
                                            let d = new Date(dateStr);
                                            if (!isNaN(d.getTime())) return d;
                                            const normalized = dateStr.replace(/-/g, '/');
                                            d = new Date(normalized);
                                            return isNaN(d.getTime()) ? null : d;
                                        };

                                        const pDate = resData.pickup_date ? parseDate(resData.pickup_date) : null;
                                        const rDate = resData.return_date ? parseDate(resData.return_date) : null;
                                        
                                        if (pDate && rDate) {
                                            const fmt = (d) => `${(d.getMonth()+1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                                            return (
                                                <>
                                                    <span className="line-period-item"><span className="line-period-icon pickup">↑</span>{fmt(pDate)}</span>
                                                    <span className="line-period-item"><span className="line-period-icon return">↓</span>{fmt(rDate)}</span>
                                                </>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', fontSize: '10px', fontWeight: 800, paddingLeft: '4px' }}>
                                    <span style={{ color: (resData.payment === true || resData.payment === 1 || resData.payment === '1') ? '#10b981' : '#ef4444' }}>
                                        {(resData.payment === true || resData.payment === 1 || resData.payment === '1') ? '支払済' : '未払'}
                                    </span>
                                    <span style={{ color: (resData.agreement === true || resData.agreement === 1 || resData.agreement === '1') ? '#10b981' : '#94a3b8' }}>
                                        {(resData.agreement === true || resData.agreement === 1 || resData.agreement === '1') ? 'フォームOK' : 'フォーム未対応'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="line-tag-container" style={{ marginBottom: '12px' }}>
                    {user.tags?.map(tag => (
                        <span 
                            key={tag.id} 
                            className="line-tag" 
                            style={{ background: tag.color + '22', color: tag.color, border: `1px solid ${tag.color}44` }}
                        >
                            {tag.name}
                        </span>
                    ))}
                </div>

                <div className="line-card-bubble">
                    <div className={`line-last-message ${(optimisticMessages[user.line_user_id]?.length > 0) ? 'optimistic' : ''}`}>
                        {optimisticMessages[user.line_user_id]?.slice(-1)[0]?.content || user.last_message_content || 'メッセージはありません'}
                    </div>
                </div>
                <div className="line-card-footer">
                    <div className="line-time">
                        {optimisticMessages[user.line_user_id]?.length > 0 
                            ? (optimisticMessages[user.line_user_id].slice(-1)[0].status === 'sent' ? '送信済' : '送信中...') 
                            : (user.last_message_at ? `${new Date(user.last_message_at.replace(/-/g, '/')).toLocaleDateString([], { month: '2-digit', day: '2-digit' })} ${new Date(user.last_message_at.replace(/-/g, '/')).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '---')}
                    </div>
                    {user.is_confirmed && (
                        <div className="line-confirmed">
                            確認済み <CheckCircleIcon sx={{ fontSize: 14 }} />
                        </div>
                    )}
                    {instance.instance_reservation_id && (
                        <div 
                            className="line-detail-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedResForEditor(instance.instance_res_data);
                            }}
                        >
                            詳細
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderListRow = (instance) => {
        const isSelected = selectedUserIds.includes(instance.instanceKey);
        const user = instance;
        const resData = instance.instance_res_data;
        return (
            <div 
                key={instance.instanceKey} 
                className={`line-list-item ${user.unread_count > 0 ? 'unread' : ''} ${multiSelectMode && isSelected ? 'selected' : ''}`}
                onClick={(e) => {
                    if (e.metaKey) {
                        handleStaffChatRegistration(instance);
                    } else if (multiSelectMode) {
                        toggleUserSelection(instance.instanceKey);
                    } else {
                        handleUserSelect(instance.instanceKey);
                    }
                }}
            >
                {multiSelectMode && (
                    <div style={{ marginRight: '15px' }}>
                        <div style={{ 
                            width: '20px', 
                            height: '20px', 
                            borderRadius: '50%', 
                            border: '2px solid #cbd5e1',
                            background: isSelected ? '#2563eb' : 'white',
                            borderColor: isSelected ? '#2563eb' : '#cbd5e1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px'
                        }}>
                            {isSelected && '✓'}
                        </div>
                    </div>
                )}
                <div className="line-list-left">
                    <LazyAvatar 
                        user={user}
                        syncProfile={syncProfile}
                        sx={{ width: 40, height: 40, borderRadius: '10px' }}
                    />
                        <div className="line-list-names">
                            <div className="line-primary-name-row">
                                <div className="line-primary-name">
                                    {resData?.customer_name || user.custom_display_name || user.line_display_name || 'Unknown'}
                                </div>
                            </div>
                            {instance.instance_reservation_id && resData && (
                                <>
                                    <div className="line-usage-period" style={{ marginTop: '0', background: 'transparent', padding: '0', fontSize: '11px' }}>
                                        {(() => {
                                            const parseDate = (dateStr) => {
                                                if (!dateStr || typeof dateStr !== 'string') return null;
                                                // Try direct parse (works for ISO8601)
                                                let d = new Date(dateStr);
                                                if (!isNaN(d.getTime())) return d;
                                                
                                                // Fallback for MySQL format (YYYY-MM-DD HH:MM:SS) -> (YYYY/MM/DD HH:MM:SS)
                                                const normalized = dateStr.replace(/-/g, '/');
                                                d = new Date(normalized);
                                                return isNaN(d.getTime()) ? null : d;
                                            };

                                            const pDate = resData.pickup_date ? parseDate(resData.pickup_date) : null;
                                            const rDate = resData.return_date ? parseDate(resData.return_date) : null;
                                            
                                            if (pDate && rDate) {
                                                const fmt = (d) => `${(d.getMonth()+1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                                                return (
                                                    <>
                                                        <span className="line-period-item"><span className="line-period-icon pickup">↑</span>{fmt(pDate)}</span>
                                                        <span className="line-period-item"><span className="line-period-icon return">↓</span>{fmt(rDate)}</span>
                                                    </>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', fontSize: '10px', fontWeight: 800, paddingLeft: '0', marginTop: '2px' }}>
                                        <span style={{ color: (resData.payment === true || resData.payment === 1 || resData.payment === '1') ? '#10b981' : '#ef4444' }}>
                                            {(resData.payment === true || resData.payment === 1 || resData.payment === '1') ? '支払済' : '未払'}
                                        </span>
                                        <span style={{ color: (resData.agreement === true || resData.agreement === 1 || resData.agreement === '1') ? '#10b981' : '#94a3b8' }}>
                                            {(resData.agreement === true || resData.agreement === 1 || resData.agreement === '1') ? 'フォームOK' : 'フォーム未対応'}
                                        </span>
                                    </div>
                                </>
                            )}
                            <div className="line-secondary-name">{user.line_display_name}</div>
                        </div>
                    </div>

                <div className="line-list-badges">
                    {user.is_pinned && (
                        <div className="line-mini-badge pinned">ピン</div>
                    )}
                    {user.hasOtherReservations && (
                        <div className="line-mini-badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>他予約有</div>
                    )}
                    {user.reservation_statuses?.map((st, idx) => {
                        const classMap = {
                            '本日貸出': 'today-pickup',
                            '本日返却': 'today-return',
                            '明日貸出': 'tomorrow-pickup',
                            '明日返却': 'tomorrow-return',
                            '利用中': 'in-use'
                        };
                        const cls = classMap[st] || st.toLowerCase().replace(/\s+/g, '-');
                        return (
                            <div key={idx} className={`line-mini-badge ${cls}`}>
                                {st}
                            </div>
                        );
                    })}
                    {user.alert_level > 0 && (
                        <Tooltip title={getAlertTooltip(user.alert_level)}>
                            <div className="line-mini-badge" style={{
                                background: getAlertColor(user.alert_level) || undefined,
                                color: 'white',
                                border: 'none',
                            }}>
                                {getAlertLabel(user.alert_level)}
                            </div>
                        </Tooltip>
                    )}
                </div>

                <div className="line-list-message-cell">
                    <div className="line-last-message">
                        {optimisticMessages[user.line_user_id]?.slice(-1)[0]?.content || user.last_message_content || '---'}
                    </div>
                </div>

                <div className="line-list-right">
                    {instance.instance_reservation_id && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedResForEditor(instance.instance_res_data);
                            }}
                            sx={{ mr: 1, borderRadius: '6px', fontSize: '11px', py: 0, minWidth: 'auto', px: 1, height: '24px' }}
                        >
                            詳細
                        </Button>
                    )}
                    <div className="line-list-time">
                        {user.last_message_at ? `${new Date(user.last_message_at.replace(/-/g, '/')).toLocaleDateString([], { month: '2-digit', day: '2-digit' })} ${new Date(user.last_message_at.replace(/-/g, '/')).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                    </div>
                    {user.unread_count > 0 && <div className="line-list-unread-badge">{user.unread_count}</div>}
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            togglePin(user.line_user_id, !user.is_pinned);
                        }}
                        sx={{ color: user.is_pinned ? '#f97316' : '#cbd5e1', ml: 0.5 }}
                    >
                        <PushPinIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </div>
            </div>
        );
    };

    return (
        <div className="line-chat-board">
            {/* Auto-archive toasts */}
            {archiveToasts.length > 0 && (
                <Box sx={{
                    position: 'fixed', top: 16, right: 16, zIndex: 13000,
                    display: 'flex', flexDirection: 'column', gap: 1,
                    pointerEvents: 'none',
                }}>
                    {archiveToasts.map((t) => (
                        <Box key={t.id} sx={{
                            background: '#fffbeb', border: '1px solid #fcd34d',
                            borderRadius: '10px', px: 2, py: 1, boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                            display: 'flex', alignItems: 'center', gap: 1,
                            animation: 'slideInRight 0.25s ease-out',
                            pointerEvents: 'auto',
                        }}>
                            <ArchiveIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#92400e', fontSize: '13px' }}>
                                {t.name} がアーカイブされました。
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            <style>{`
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(40px); }
                    to { opacity: 1; transform: translateX(0); }
                }
            `}</style>

            <header className="line-board-header">
                <div className="line-board-title">
                    <ChatIcon color="primary" />
                    <span>チャット・ボード</span>
                    <Tooltip title="使い方ガイド">
                        <IconButton 
                            onClick={() => setShowBoardHelp(true)} 
                            sx={{ color: '#64748b', ml: 1 }}
                        >
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                </div>
                
                <IconButton 
                    className="line-mobile-menu-trigger"
                    onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                    sx={{ ml: 'auto' }}
                >
                    <MoreVertIcon />
                </IconButton>

                <div className="line-board-controls">
                    <Box display="flex" alignItems="center" gap={3}>
                        <Box display="flex" alignItems="center" sx={{ background: '#f1f5f9', px: 2, py: 0.5, borderRadius: '20px' }}>
                            <Typography variant="caption" sx={{ mr: 1, fontWeight: 800, color: '#64748b' }}>
                                既読モード
                            </Typography>
                            <Switch 
                                size="small"
                                checked={readMode}
                                onChange={(e) => setReadMode(e.target.checked)}
                            />
                        </Box>
                    </Box>
                    
                    <div className="line-view-toggle">
                        <IconButton 
                            size="small" 
                            onClick={() => setViewMode('grid')}
                            className={viewMode === 'grid' ? 'active' : ''}
                            sx={{ color: viewMode === 'grid' ? '#2563eb' : '#94a3b8' }}
                        >
                            <ViewComfyIcon />
                        </IconButton>
                        <IconButton 
                            size="small" 
                            onClick={() => setViewMode('list')}
                            className={viewMode === 'list' ? 'active' : ''}
                            sx={{ color: viewMode === 'list' ? '#2563eb' : '#94a3b8' }}
                        >
                            <ViewListIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                        {!multiSelectMode ? (
                            <Button 
                                variant="outlined" 
                                size="small"
                                onClick={() => { setMultiSelectMode(true); setSelectedUserIds([]); }}
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#e2e8f0', color: '#1e293b' }}
                            >
                                複数選択
                            </Button>
                        ) : (
                            <>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={handleSelectAll}
                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, mr: 1 }}
                                >
                                    全選択
                                </Button>
                                <Button 
                                    variant="contained" 
                                    size="small"
                                    color="primary"
                                    disabled={selectedUserIds.length === 0}
                                    onClick={() => { 
                                        loadTemplatesData();
                                        setShowBroadcastModal(true); 
                                    }}
                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                >
                                    一斉送信 ({selectedUserIds.length})
                                </Button>
                                <Button 
                                    variant="text" 
                                    size="small"
                                    onClick={() => setMultiSelectMode(false)}
                                    sx={{ color: '#64748b' }}
                                >
                                    キャンセル
                                </Button>
                            </>
                        )}
                    </div>
                    
                    <Button 
                        variant="outlined" 
                        startIcon={<RefreshIcon />} 
                        onClick={handleRefresh}
                        className="RefreshButton"
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#e2e8f0', color: '#1e293b' }}
                    >
                        最新の状態に更新
                    </Button>

                    <Menu
                        anchorEl={mobileMenuAnchor}
                        open={Boolean(mobileMenuAnchor)}
                        onClose={() => setMobileMenuAnchor(null)}
                        PaperProps={{
                            sx: { borderRadius: '12px', minWidth: '200px', mt: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                        }}
                    >
                        <MuiMenuItem sx={{ py: 1.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <Typography variant="body2" fontWeight={800}>既読モード</Typography>
                                <Switch 
                                    size="small"
                                    checked={readMode}
                                    onChange={(e) => setReadMode(e.target.checked)}
                                />
                            </Box>
                        </MuiMenuItem>
                        <MuiMenuItem sx={{ py: 1.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <Typography variant="body2" fontWeight={800}>定期読み込み</Typography>
                                <Switch 
                                    size="small"
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                />
                            </Box>
                        </MuiMenuItem>
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); setMultiSelectMode(!multiSelectMode); setSelectedUserIds([]); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <CheckCircleIcon fontSize="small" color={multiSelectMode ? "primary" : "inherit"} />
                                <Typography variant="body2" fontWeight={800}>{multiSelectMode ? '複数選択解除' : '複数選択モード'}</Typography>
                            </Box>
                        </MuiMenuItem>
                        {isMobile && multiSelectMode && (
                            <MuiMenuItem 
                                disabled={selectedUserIds.length === 0}
                                onClick={() => { 
                                    setMobileMenuAnchor(null);
                                    loadTemplatesData();
                                    setShowBroadcastModal(true); 
                                }}
                                sx={{ color: '#2563eb' }}
                            >
                                <Box display="flex" alignItems="center" gap={1}>
                                    <SendIcon fontSize="small" />
                                    <Typography variant="body2" fontWeight={800}>一斉送信 ({selectedUserIds.length})</Typography>
                                </Box>
                            </MuiMenuItem>
                        )}
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); setShowTemplateManager(true); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <SettingsSuggestIcon fontSize="small" />
                                <Typography variant="body2" fontWeight={800}>テンプレート管理</Typography>
                            </Box>
                        </MuiMenuItem>
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); window.open('/ai_chat_review', '_blank'); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <SmartToyIcon fontSize="small" />
                                <Typography variant="body2" fontWeight={800}>AIチャットレビュー</Typography>
                            </Box>
                        </MuiMenuItem>
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); setViewMode(viewMode === 'grid' ? 'list' : 'grid'); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                {viewMode === 'grid' ? <ViewListIcon fontSize="small" /> : <ViewComfyIcon fontSize="small" />}
                                <Typography variant="body2" fontWeight={800}>{viewMode === 'grid' ? 'リスト表示に切替' : 'グリッド表示に切替'}</Typography>
                            </Box>
                        </MuiMenuItem>
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); handleRefresh(); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <RefreshIcon fontSize="small" />
                                <Box>
                                    <Typography variant="body2" fontWeight={800}>最新の状態に更新</Typography>
                                    {showArchived && (
                                        <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700, lineHeight: 1 }}>（アーカイブを含む）</Typography>
                                    )}
                                </Box>
                            </Box>
                        </MuiMenuItem>
                        <MuiMenuItem sx={{ py: 1.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <Box display="flex" alignItems="center" gap={1}>
                                    <ArchiveIcon fontSize="small" color={showArchived ? "warning" : "inherit"} />
                                    <Typography variant="body2" fontWeight={800}>アーカイブ表示</Typography>
                                </Box>
                                <Switch
                                    size="small"
                                    checked={showArchived}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setMobileMenuAnchor(null);
                                            setShowArchiveConfirm(true);
                                        } else {
                                            setShowArchived(false);
                                            localStorage.setItem('line_show_archived', 'off');
                                        }
                                    }}
                                />
                            </Box>
                        </MuiMenuItem>
                    </Menu>
                </div>
            </header>

            <div className="line-filters-container">
                <Box display="flex" flexDirection="column" gap={2} mb={1}>
                    {/* 1行目: フィルターチップ */}
                    <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                        <div className="line-filter-chips">
                            {[
                                { id: 'all', label: 'すべて', icon: <SearchIcon fontSize="small" /> },
                                { id: 'pinned', label: 'ピン留め', icon: <PushPinIcon fontSize="small" /> },
                                { id: 'today_pickup', label: '本日貸出', icon: <RefreshIcon fontSize="small" /> },
                                { id: 'today_return', label: '本日返却', icon: <CheckCircleIcon fontSize="small" /> },
                                { id: 'tomorrow_pickup', label: '翌日貸出', icon: <RefreshIcon fontSize="small" /> },
                                { id: 'tomorrow_return', label: '翌日返却', icon: <CheckCircleIcon fontSize="small" /> },
                                { id: 'in_use', label: '貸出中', icon: <RefreshIcon fontSize="small" /> },
                                { id: 'unlinked', label: '未紐付け', icon: <PersonIcon fontSize="small" /> },
                            ].map(chip => (
                                <div 
                                    key={chip.id} 
                                    className={`line-filter-chip ${
                                        chip.id === 'all'
                                            ? (filters.length === 0 && !customDateFilter && selectedTagFilters.length === 0 ? 'active' : '')
                                            : (filters.includes(chip.id) ? 'active' : '')
                                    }`}
                                    onClick={() => { toggleFilter(chip.id); if (chip.id === 'all') setSelectedTagFilters([]); }}
                                >
                                    {chip.icon}
                                    {chip.label}
                                </div>
                            ))}

                            {/* Tag filter chip */}
                            <div
                                className={`line-filter-chip ${selectedTagFilters.length > 0 ? 'active' : ''}`}
                                onClick={(e) => setTagFilterAnchor(e.currentTarget)}
                            >
                                <LocalOfferIcon fontSize="small" />
                                {selectedTagFilters.length > 0 ? `タグ (${selectedTagFilters.length})` : 'タグ'}
                            </div>
                            <Popover
                                open={Boolean(tagFilterAnchor)}
                                anchorEl={tagFilterAnchor}
                                onClose={() => setTagFilterAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                PaperProps={{ sx: { p: 2, borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', minWidth: 200 } }}
                            >
                                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>タグで絞り込み</Typography>
                                {tags && tags.length > 0 ? (
                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                        {tags.map(tag => {
                                            const isSelected = selectedTagFilters.includes(tag.id);
                                            return (
                                                <Box
                                                    key={tag.id}
                                                    onClick={() => setSelectedTagFilters(prev =>
                                                        isSelected ? prev.filter(id => id !== tag.id) : [...prev, tag.id]
                                                    )}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        px: 1.5, py: 0.8, borderRadius: '8px', cursor: 'pointer',
                                                        background: isSelected ? tag.color + '18' : 'transparent',
                                                        border: isSelected ? `1px solid ${tag.color}` : '1px solid transparent',
                                                        '&:hover': { background: tag.color + '10' },
                                                    }}
                                                >
                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: tag.color, flexShrink: 0 }} />
                                                    <Typography variant="body2" fontWeight={isSelected ? 700 : 500}>{tag.name}</Typography>
                                                </Box>
                                            );
                                        })}
                                        {selectedTagFilters.length > 0 && (
                                            <Button size="small" onClick={() => setSelectedTagFilters([])} sx={{ mt: 0.5, fontWeight: 700 }}>
                                                クリア
                                            </Button>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">タグがありません</Typography>
                                )}
                            </Popover>

                            <div
                                className={`line-filter-chip ${customDateFilter ? 'active' : ''}`}
                                onClick={(e) => setDateAnchor(e.currentTarget)}
                            >
                                <CalendarTodayIcon fontSize="small" />
                                {customDateFilter ? `${customDateFilter.date} (${customDateFilter.type === 'pickup' ? '貸出' : '返却'})` : '日付選択'}
                            </div>

                            <Popover
                                open={Boolean(dateAnchor)}
                                anchorEl={dateAnchor}
                                onClose={() => setDateAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                PaperProps={{ sx: { p: 2, borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)' } }}
                            >
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Typography variant="subtitle2" fontWeight={800}>日付を指定して絞り込み</Typography>
                                    <input 
                                        type="date" 
                                        value={tempDate} 
                                        onChange={(e) => setTempDate(e.target.value)}
                                        style={{ 
                                            padding: '8px', 
                                            borderRadius: '8px', 
                                            border: '1px solid #cbd5e1',
                                            fontSize: '14px',
                                            fontFamily: 'inherit'
                                        }}
                                    />
                                    <Box display="flex" gap={1}>
                                        <Button 
                                            variant="contained" 
                                            fullWidth 
                                            size="small"
                                            onClick={() => {
                                                setCustomDateFilter({ date: tempDate, type: 'pickup' });
                                                setDateAnchor(null);
                                            }}
                                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                                        >
                                            貸出
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            fullWidth 
                                            size="small"
                                            onClick={() => {
                                                setCustomDateFilter({ date: tempDate, type: 'return' });
                                                setDateAnchor(null);
                                            }}
                                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                                        >
                                            返却
                                        </Button>
                                    </Box>
                                    <Button 
                                        size="small" 
                                        color="error" 
                                        onClick={() => {
                                            setCustomDateFilter(null);
                                            setDateAnchor(null);
                                        }}
                                    >
                                        解除
                                    </Button>
                                </Box>
                            </Popover>
                        </div>
                    </Box>

                    {/* 2行目: 検索窓とテンプレート管理 */}
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        <div className="line-search-wrapper" style={{ margin: 0, flex: 1, minWidth: '300px' }}>
                            <SearchIcon className="line-search-icon" />
                            <input 
                                type="text" 
                                className="line-search-input" 
                                placeholder="顧客名・LINE名・メッセージを検索.." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        {searchIncludedArchived && (
                            <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                アーカイブからも検索しました
                            </Typography>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<SettingsSuggestIcon />}
                            onClick={() => setShowTemplateManager(true)}
                            color="info"
                            className="desktop-only"
                            sx={{ 
                                borderRadius: '12px', 
                                textTransform: 'none', 
                                fontWeight: 800, 
                                height: '45px', 
                                border: '1px solid #e2e8f0', 
                                background: 'white',
                                color: '#1e293b',
                                whiteSpace: 'nowrap',
                                ml: 2
                            }}
                        >
                            テンプレート管理
                        </Button>
                    </Box>
                </Box>
            </div>

            {loading && chatInstances.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>読み込み中...</Box>
            ) : (
                <div className={viewMode === 'grid' ? "line-chat-grid" : "line-chat-list"}>
                    {filteredInstances.slice(0, visibleCount).map(instance => (
                        viewMode === 'grid' ? renderChatCard(instance) : renderListRow(instance)
                    ))}
                </div>
            )}

            {isMobile && multiSelectMode && (
                <div className="line-mobile-sticky-bar">
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#f8fafc' }}>
                            {selectedUserIds.length} 名を選択中
                        </Typography>
                        <Box display="flex" gap={1}>
                            <Button 
                                variant="contained" 
                                color="success"
                                size="small"
                                disabled={selectedUserIds.length === 0}
                                onClick={() => { 
                                    loadTemplatesData();
                                    setShowBroadcastModal(true); 
                                }}
                                startIcon={<SendIcon />}
                                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 800, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                            >
                                一斉送信
                            </Button>
                            <IconButton 
                                size="small" 
                                onClick={() => setMultiSelectMode(false)}
                                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </div>
            )}

            {/* Template Manager Dialog */}
            <Dialog 
                open={showTemplateManager} 
                onClose={() => setShowTemplateManager(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <SettingsSuggestIcon color="primary" />
                            <span>メッセージテンプレート管理</span>
                        </Box>
                        <IconButton onClick={() => setShowTemplateManager(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <div style={{ display: 'flex', gap: 0, height: '600px' }}>
                        
                        {/* 1. カテゴリ管理 (左カラム) */}
                        <div style={{ width: '220px', borderRight: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
                            <Box p={2} borderBottom="1px solid #e2e8f0" display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={800}>カテゴリ</Typography>
                                <IconButton size="small" onClick={() => { setEditCategory({ id: null, name: '' }); setShowCategoryForm(true); }}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            
                            {showCategoryForm && (
                                <Box p={2} borderBottom="1px solid #e2e8f0" bgcolor="white">
                                    <TextField 
                                        size="small" 
                                        fullWidth 
                                        label="カテゴリ名"
                                        value={editCategory.name}
                                        onChange={(e) => setEditCategory(prev => ({ ...prev, name: e.target.value }))}
                                        autoFocus
                                    />
                                    <Box mt={1} display="flex" gap={1}>
                                        <Button size="small" variant="contained" fullWidth onClick={handleSaveCategory}>保存</Button>
                                        <Button size="small" variant="outlined" fullWidth onClick={() => setShowCategoryForm(false)}>消去</Button>
                                    </Box>
                                </Box>
                            )}

                            <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                                <div 
                                    onClick={() => setSelectedCategoryId(null)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        marginBottom: '4px',
                                        background: selectedCategoryId === null ? '#eef2ff' : 'transparent',
                                        color: selectedCategoryId === null ? '#4f46e5' : '#475569',
                                        fontWeight: selectedCategoryId === null ? 800 : 400,
                                        fontSize: '14px'
                                    }}
                                >
                                    すべて表示
                                </div>
                                <div 
                                    onClick={() => setSelectedCategoryId('none')}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        marginBottom: '4px',
                                        background: selectedCategoryId === 'none' ? '#eef2ff' : 'transparent',
                                        color: selectedCategoryId === 'none' ? '#4f46e5' : '#475569',
                                        fontWeight: selectedCategoryId === 'none' ? 800 : 400,
                                        fontSize: '14px'
                                    }}
                                >
                                    未分類
                                </div>
                                
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
                                    <SortableContext items={categories.map(c => String(c.id))} strategy={verticalListSortingStrategy}>
                                        {categories.map(cat => (
                                            <CategorySortableItem 
                                                key={cat.id} 
                                                id={String(cat.id)} 
                                                name={cat.name}
                                                isSelected={selectedCategoryId === cat.id}
                                                onClick={() => setSelectedCategoryId(cat.id)}
                                                onEdit={() => { setEditCategory(cat); setShowCategoryForm(true); }}
                                                onDelete={() => handleDeleteCategory(cat.id)}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                        </div>

                        {/* 2. テンプレート一覧 (中カラム) */}
                        <div style={{ width: '280px', borderRight: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column' }}>
                            <Box p={2} borderBottom="1px solid #f1f5f9" display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={800}>テンプレート</Typography>
                                <Button 
                                    size="small" 
                                    variant="contained" 
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        setSelectedTemplate(null);
                                        setEditTemplate({ name: '', content: '', category_id: selectedCategoryId === 'none' ? null : selectedCategoryId });
                                    }}
                                    sx={{ borderRadius: '8px', fontWeight: 800, textTransform: 'none' }}
                                >
                                    新規
                                </Button>
                            </Box>
                            
                            <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTemplateDragEnd}>
                                    <SortableContext 
                                        items={templates
                                            .filter(t => 
                                                selectedCategoryId === null ? true : 
                                                selectedCategoryId === 'none' ? t.category_id === null : 
                                                String(t.category_id) === String(selectedCategoryId)
                                            )
                                            .sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0))
                                            .map(t => String(t.id))} 
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {templates
                                            .filter(t => 
                                                selectedCategoryId === null ? true : 
                                                selectedCategoryId === 'none' ? t.category_id === null : 
                                                String(t.category_id) === String(selectedCategoryId)
                                            )
                                            .sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0))
                                            .map(t => (
                                                <SortableItem
                                                    key={t.id}
                                                    id={String(t.id)}
                                                    isSelected={selectedTemplate?.id === t.id}
                                                    onClick={() => {
                                                        setSelectedTemplate(t);
                                                        setEditTemplate({ ...t });
                                                    }}
                                                    onDelete={undefined as any}
                                                >
                                                    <div style={{ fontWeight: 800, fontSize: '14px', color: '#1e293b' }}>{t.name}</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {t.content}
                                                    </div>
                                                </SortableItem>
                                            ))
                                        }
                                    </SortableContext>
                                </DndContext>
                            </div>
                        </div>

                        {/* 3. 編集エリア (右カラム) */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '24px', gap: '20px' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="h6" fontWeight={800} color="primary">
                                        {selectedTemplate ? 'テンプレート編集' : '新規テンプレート作成'}
                                    </Typography>
                                    <Tooltip title="テンプレートの使い方">
                                        <IconButton size="small" onClick={() => setShowHelp(true)}>
                                            <HelpOutlineIcon sx={{ fontSize: 20, color: '#64748b' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            
                            <Box display="flex" gap={2}>
                                <TextField 
                                    label="テンプレート名"
                                    fullWidth
                                    value={editTemplate.name}
                                    onChange={(e) => setEditTemplate(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="例: 到着連絡"
                                />
                                <FormControl sx={{ minWidth: 150 }}>
                                    <InputLabel>カテゴリ</InputLabel>
                                    <Select
                                        value={editTemplate.category_id || ''}
                                        label="カテゴリ"
                                        onChange={(e) => setEditTemplate(prev => ({ ...prev, category_id: e.target.value || null }))}
                                    >
                                        <MuiMenuItem value=""><em>未分類</em></MuiMenuItem>
                                        {categories.map(c => (
                                            <MuiMenuItem key={c.id} value={c.id}>{c.name}</MuiMenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            
                            <Box flex={1} display="flex" flexDirection="column">
                                <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" fontWeight={800} color="textSecondary">内容</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        変数: {'{customer_name}, {start_date_time}, {end_date_time}, {form}'}
                                    </Typography>
                                </Box>
                                <textarea 
                                    rows={8}
                                    value={editTemplate.content}
                                    onChange={(e) => setEditTemplate(prev => ({ ...prev, content: e.target.value }))}
                                    placeholder="メッセージ内容を入力..."
                                    style={{ 
                                        width: '100%', 
                                        padding: '16px', 
                                        borderRadius: '12px', 
                                        border: '1px solid #cbd5e1', 
                                        fontSize: '15px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        lineHeight: 1.6,
                                        boxSizing: 'border-box',
                                        minHeight: '200px'
                                    }}
                                />
                            </Box>

                            <Box display="flex" justifyContent="space-between" pt={2} borderTop="1px solid #f1f5f9">
                                {selectedTemplate && (
                                    <Button 
                                        color="error" 
                                        variant="outlined" 
                                        startIcon={<DeleteIcon />}
                                        onClick={async () => {
                                            if (window.confirm('このテンプレートを削除しますか？')) {
                                                const success = await deleteTemplate(selectedTemplate.id);
                                                if (success) {
                                                    showMessage('テンプレートを削除しました');
                                                    loadTemplatesData();
                                                    setSelectedTemplate(null);
                                                    setEditTemplate({ name: '', content: '', category_id: null });
                                                } else {
                                                    showMessage('削除に失敗しました', 'error');
                                                }
                                            }
                                        }}
                                        sx={{ borderRadius: '10px' }}
                                    >
                                        削除
                                    </Button>
                                )}
                                <Box flex={1} />
                                <Button 
                                    variant="contained" 
                                    disabled={!editTemplate.name.trim() || !editTemplate.content.trim()}
                                    onClick={async () => {
                                        const success = await saveTemplate(editTemplate);
                                        if (success) {
                                            showMessage('保存しました');
                                            loadTemplatesData();
                                            // 保存後にフォームをクリア
                                            setSelectedTemplate(null);
                                            setEditTemplate({ name: '', content: '', category_id: null });
                                        } else {
                                            showMessage('保存に失敗しました', 'error');
                                        }
                                    }}
                                    sx={{ borderRadius: '10px', px: 6, py: 1, fontWeight: 800, fontSize: '16px' }}
                                >
                                    保存する
                                </Button>
                            </Box>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Broadcast Modal */}
            <Dialog
                open={showBroadcastModal}
                onClose={() => { setShowBroadcastModal(false); setBroadcastStep('compose'); }}
                maxWidth={broadcastStep === 'preview' ? 'lg' : 'sm'}
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <span>
                            {broadcastStep === 'compose'
                                ? `一斉メッセージ送信 (${selectedUserIds.length}名)`
                                : `送信プレビュー (${preparedMessages.length}名)`
                            }
                        </span>
                        <IconButton onClick={() => { setShowBroadcastModal(false); setBroadcastStep('compose'); }} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {broadcastStep === 'compose' ? (
                        <>
                            <Box mt={2} mb={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>テンプレートを選択</InputLabel>
                                    <Select
                                        value=""
                                        label="テンプレートを選択"
                                        onChange={(e) => {
                                            const template = templates.find(t => t.id === e.target.value);
                                            if (template) setBroadcastText(template.content);
                                        }}
                                    >
                                        <MuiMenuItem value="">
                                            <em>選択なし</em>
                                        </MuiMenuItem>
                                        {categories.map(cat => [
                                            <MuiMenuItem key={`cat-${cat.id}`} disabled sx={{ fontWeight: 800, background: '#f8fafc', color: '#64748b' }}>
                                                {cat.name}
                                            </MuiMenuItem>,
                                            ...templates
                                                .filter(t => t.category_id == cat.id)
                                                .map(t => (
                                                    <MuiMenuItem key={t.id} value={t.id} sx={{ pl: 4 }}>
                                                        {t.name}
                                                    </MuiMenuItem>
                                                ))
                                        ])}
                                        <MuiMenuItem disabled sx={{ fontWeight: 800, background: '#f8fafc', color: '#64748b' }}>
                                            未分類
                                        </MuiMenuItem>
                                        {templates
                                            .filter(t => !t.category_id)
                                            .map(t => (
                                                <MuiMenuItem key={t.id} value={t.id} sx={{ pl: 4 }}>
                                                    {t.name}
                                                </MuiMenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            </Box>
                            <div style={{ padding: '0 0' }}>
                                <textarea
                                    rows={8}
                                    value={broadcastText}
                                    onChange={(e) => setBroadcastText(e.target.value)}
                                    placeholder="送信するメッセージを入力してください..."
                                    style={{
                                        width: '100%',
                                        minHeight: '200px',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        marginBottom: '8px',
                                        resize: 'vertical'
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                    ※ {`{customer_name}`}, {`{end_date_time}`} などの変数は送信時に各ユーザーの情報に置換されます。
                                </Typography>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        onClick={() => { setShowBroadcastModal(false); setBroadcastStep('compose'); }}
                                    >
                                        キャンセル
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={!broadcastText.trim()}
                                        onClick={handleShowPreview}
                                    >
                                        プレビュー
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* プレビュー画面: 3列×5行グリッド */}
                            {(() => {
                                const ITEMS_PER_PAGE = 15;
                                const totalPages = Math.ceil(preparedMessages.length / ITEMS_PER_PAGE);
                                const startIdx = previewPage * ITEMS_PER_PAGE;
                                const pageItems = preparedMessages.slice(startIdx, startIdx + ITEMS_PER_PAGE);

                                return (
                                    <Box>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(3, 1fr)',
                                            gap: 1.5,
                                            mt: 1,
                                        }}>
                                            {pageItems.map((msg, i) => {
                                                const globalIdx = startIdx + i;
                                                return (
                                                    <Box key={msg.instanceKey} sx={{
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        p: 1,
                                                        backgroundColor: '#fafafa',
                                                    }}>
                                                        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5, color: '#1976d2' }}>
                                                            {msg.name}
                                                        </Typography>
                                                        <textarea
                                                            value={msg.text}
                                                            onChange={(e) => {
                                                                setPreparedMessages(prev => {
                                                                    const updated = [...prev];
                                                                    updated[globalIdx] = { ...updated[globalIdx], text: e.target.value };
                                                                    return updated;
                                                                });
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                minHeight: '120px',
                                                                padding: '6px',
                                                                borderRadius: '4px',
                                                                border: '1px solid #cbd5e1',
                                                                fontSize: '11px',
                                                                fontFamily: 'inherit',
                                                                resize: 'vertical',
                                                                lineHeight: 1.4,
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>

                                        {/* ページネーション */}
                                        {totalPages > 1 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                                                <Button
                                                    size="small"
                                                    disabled={previewPage === 0}
                                                    onClick={() => setPreviewPage(p => p - 1)}
                                                >
                                                    ← 前へ
                                                </Button>
                                                <Typography variant="body2" color="text.secondary">
                                                    {previewPage + 1} / {totalPages} ページ
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    disabled={previewPage >= totalPages - 1}
                                                    onClick={() => setPreviewPage(p => p + 1)}
                                                >
                                                    次へ →
                                                </Button>
                                            </Box>
                                        )}

                                        {/* アクションボタン */}
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                                            <Button
                                                variant="outlined"
                                                color="inherit"
                                                onClick={() => setBroadcastStep('compose')}
                                            >
                                                ← 戻る
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={isSendingBroadcast}
                                                onClick={handleSendBroadcast}
                                            >
                                                {isSendingBroadcast ? '送信中...' : `${preparedMessages.length}件を送信する`}
                                            </Button>
                                        </div>
                                    </Box>
                                );
                            })()}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Chat Dialog */}
            <Dialog
                open={Boolean(selectedInstanceKey)}
                onClose={() => { setSelectedInstanceKey(null); setOpenLineUserId(null); }}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                sx={{ zIndex: 10500 }}
                PaperProps={{
                    sx: {
                        height: isMobile ? '100%' : '85vh',
                        borderRadius: isMobile ? 0 : '16px',
                        overflow: 'visible',
                        position: 'relative'
                    }
                }}
            >
                {/* 閉じるボタン（モーダルの外側、または上端に配置） */}
                <IconButton
                    onClick={() => setSelectedInstanceKey(null)}
                    sx={{
                        position: 'absolute',
                        right: isMobile ? 8 : -48,
                        top: isMobile ? 8 : 0,
                        zIndex: 2000,
                        color: isMobile ? '#64748b' : 'white',
                        bgcolor: isMobile ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.3)',
                        '&:hover': { 
                            bgcolor: isMobile ? 'white' : 'rgba(0,0,0,0.5)',
                            color: isMobile ? '#000' : 'white'
                        },
                        boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
                    {selectedInstanceKey && (() => {
                        let instance = chatInstances.find(i => i.instanceKey === selectedInstanceKey);
                        if (!instance) {
                            // Try to find the same user even if the key changed (e.g. after linking/unlinking)
                            const [lineUserId] = selectedInstanceKey.split('_');
                            instance = chatInstances.find(i => i.line_user_id === lineUserId);
                            
                            // If we found a new instance for the same user, update the key so we don't keep falling back
                            if (instance) {
                                // Use a small timeout to avoid state update during render warning
                                setTimeout(() => setSelectedInstanceKey(instance.instanceKey), 0);
                            }
                        }
                        if (!instance) return null;
                        return (
                            <LineChatPanel
                                user={instance}
                                onReload={handleRefresh}
                                onClose={() => { setSelectedInstanceKey(null); setOpenLineUserId(null); }}
                                readMode={readMode}
                            />
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Notifications */}
            <Snackbar 
                open={notification.open} 
                autoHideDuration={4000} 
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity as any} sx={{ width: '100%', borderRadius: '12px', fontWeight: 800 }}>
                    {notification.message}
                </Alert>
            </Snackbar>
            <TemplateHelpDialog 
                open={showHelp} 
                onClose={() => setShowHelp(false)} 
            />
            <BoardHelpDialog
                open={showBoardHelp}
                onClose={() => setShowBoardHelp(false)}
            />

            {/* Archive Confirm Dialog */}
            <Dialog open={showArchiveConfirm} onClose={() => setShowArchiveConfirm(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 0 }}>アーカイブ表示</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">アーカイブも含めて読み込みしますか？</Typography>
                    <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                        <Button onClick={() => setShowArchiveConfirm(false)} size="small" sx={{ fontWeight: 700 }}>キャンセル</Button>
                        <Button
                            variant="contained" size="small" sx={{ fontWeight: 700 }}
                            onClick={() => {
                                setShowArchived(true);
                                localStorage.setItem('line_show_archived', 'on');
                                setShowArchiveConfirm(false);
                            }}
                        >読み込む</Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Staff Registration Dialog */}
            <Dialog open={!!staffRegTarget} onClose={() => setStaffRegTarget(null)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 0 }}>スタッフチャット登録</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {staffRegTarget?.line_display_name || staffRegTarget?.custom_display_name} をスタッフとして登録します。
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="スタッフ名"
                        value={staffRegName}
                        onChange={(e) => setStaffRegName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing && staffRegName.trim()) confirmStaffRegistration(); }}
                        sx={{ mb: 2 }}
                    />
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={() => setStaffRegTarget(null)} size="small" sx={{ fontWeight: 700 }}>キャンセル</Button>
                        <Button variant="contained" size="small" sx={{ fontWeight: 700 }} disabled={!staffRegName.trim()} onClick={confirmStaffRegistration}>登録</Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Reservation Detail/Editor Panel */}
            {(ReservationEditorPanel as any) && (
            <ReservationEditorPanel
                {...{
                    open: !!selectedResForEditor,
                    editing: editingResForEditor,
                    disabled: isSavingRes,
                    onCancel: () => setSelectedResForEditor(null),
                    onClose: () => setSelectedResForEditor(null),
                    onChange: (updates: any) => setEditingResForEditor(prev => ({ ...prev, ...updates })),
                    onSave: handleSaveReservation,
                    registerNameInputRef: () => {},
                    reservationStatusList: reservationStatusList,
                    vehicles: vehiclesCache,
                    vehicleTypes: vehicleTypeList,
                    onAssignClick: () => {},
                } as any}
            />
            )}

            <style>{`
                .line-detail-btn {
                    padding: 2px 8px;
                    border: 1px solid #3b82f6;
                    color: #2563eb;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.2s;
                    margin-left: auto;
                }
                .line-detail-btn:hover {
                    background: #eff6ff;
                }
                .line-other-res-badge {
                    position: absolute;
                    top: 12px;
                    left: 12px;
                    background: #fef3c7;
                    color: #92400e;
                    font-size: 10px;
                    font-weight: 800;
                    padding: 2px 6px;
                    border-radius: 4px;
                    border: 1px solid #fcd34d;
                    z-index: 10;
                }
                .line-today-tag {
                    font-size: 10px;
                    font-weight: 800;
                    padding: 1px 6px;
                    border-radius: 4px;
                    margin-left: 6px;
                    vertical-align: middle;
                }
                .line-today-tag.pickup {
                    background: #ecfdf5;
                    color: #059669;
                    border: 1px solid #10b981;
                }
                .line-today-tag.return {
                    background: #eff6ff;
                    color: #2563eb;
                    border: 1px solid #3b82f6;
                }
                .line-period-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    font-size: 10px;
                    margin-right: 4px;
                    color: white;
                }
                .line-period-icon.pickup { background: #10b981; }
                .line-period-icon.return { background: #3b82f6; }
            `}</style>
        </div>
    );
};
export default LineChatBoard;
